import { useQuery } from '@tanstack/react-query';
import { createFileRoute, useNavigate, useParams } from '@tanstack/react-router';
import { InlineMessage, Spinner, Tag, Timeline, TimelineEvent, TimelineEventCard } from '@volvo/vcdk-react';
import { useTranslation } from 'react-i18next';

import EmptyState from '@/components/EmptyState';
import { ErrorBoundary } from '@/components/ErrorBoundary';
import ProductCategory from '@/components/ProductCategory';
import { loaderUnitMaintenancePage } from '@/loaders/unit.maintenance.loader';
import {
  generateServiceTitle,
  getServiceProtocolChassisPosition,
  getType,
} from '@/routes/_authenticated/_account/fleets/$fleetId/units/$endCustomerUnitId/maintenance/_utils/serviceProtocol';
import { getServiceProtocolsQueryOptions, unitByIdQueryOptions } from '@/service/vpc/queryOptions';
import { chassis, dates, files } from '@/utils';

export const Route = createFileRoute('/_authenticated/_account/fleets/$fleetId/units/$endCustomerUnitId/maintenance/')({
  component: UnitMaintenancePage,
  errorComponent: ErrorBoundary,
  loader: loaderUnitMaintenancePage,
});

export function UnitMaintenancePage() {
  const { endCustomerUnitId, fleetId } = useParams({
    strict: false,
  });
  const navigate = useNavigate();
  const { t } = useTranslation();

  const { data: unit, isLoading: isLoadingUnit } = useQuery(
    unitByIdQueryOptions({ endCustomerUnitId: endCustomerUnitId! })
  );
  const chassisIdentifiers = chassis.getUnitsChassisIdentifier(unit) || [];

  const { data: serviceProtocols, isLoading: isLoadingServiceProtocols } = useQuery(
    getServiceProtocolsQueryOptions({ chassisIdentifiers })
  );

  if (isLoadingUnit || isLoadingServiceProtocols) {
    return (
      <div className="flex h-full w-full flex-col items-center justify-center">
        <Spinner size="medium" />
      </div>
    );
  }

  if (!serviceProtocols || serviceProtocols.length === 0) {
    return (
      <EmptyState
        title={t(($) => $.pages.unit.maintenance.empty_state.title)}
        message={t(($) => $.pages.unit.maintenance.empty_state.message)}
      />
    );
  }

  const serviceProtocolsSorted = serviceProtocols.sort(
    (a, b) => new Date(b.serviceDate).getTime() - new Date(a.serviceDate).getTime()
  );

  return (
    <>
      {unit?.claiming && (
        <InlineMessage icon="info" variant="info" className="mb-4">
          {t(($) => $.pages.unit.maintenance.unverified_ownership_notice)}
        </InlineMessage>
      )}
      <Timeline>
        {serviceProtocolsSorted.map((spa, idx) => (
          <div key={spa.protocolKey}>
            <TimelineEvent progress="completed" className="timeline-event-indicator">
              <Tag slot="indicator">
                {dates.formatDate(spa.serviceDate)}
                {spa.businessPartner?.businessPartnerName ? `, ${spa.businessPartner.businessPartnerName}` : ''}
              </Tag>
            </TimelineEvent>
            <TimelineEventCard
              progress="completed"
              noLine={idx === serviceProtocolsSorted.length - 1}
              onClick={() => {
                if (spa.serviceProtocolContentDetails?.fileName) {
                  files.downloadFile(
                    spa.serviceProtocolContentDetails.fileContent,
                    spa.serviceProtocolContentDetails.fileName
                  );
                } else {
                  navigate({
                    to: fleetId
                      ? '/fleets/$fleetId/units/$endCustomerUnitId/maintenance/$protocolKey'
                      : '/units/$endCustomerUnitId/maintenance/$protocolKey',
                    params: {
                      fleetId: fleetId || '',
                      endCustomerUnitId: endCustomerUnitId || '',
                      protocolKey: spa.protocolKey,
                    },
                  });
                }
              }}
            >
              <span slot="label">{generateServiceTitle(spa, t)}</span>
              <span slot="text">
                <div className="flex flex-col gap-4">
                  <ProductCategory
                    chassisPosition={getServiceProtocolChassisPosition(unit, spa, t)}
                    serialNumbers={
                      chassis
                        .getDriveLine(
                          unit,
                          chassis.getId({ chassisNumber: spa.chassisNumber, chassisSeries: spa.chassisSeries })
                        )
                        ?.individualProducts.map((ip) => String(ip.serialNumber)) || []
                    }
                  />
                  {(() => {
                    const displayOperations = spa.serviceActivityEventDetails.filter((detail) =>
                      detail.operationDetails.some((operation) => operation.operationStatus === 'Performed')
                    );
                    if (displayOperations.length === 0) {
                      return <></>;
                    }

                    return (
                      <div className="flex flex-row flex-wrap items-end gap-4">
                        {displayOperations.map((op, opIndex) => (
                          <Tag key={opIndex} variant="info-subtle">
                            {t(($) => $.pages.unit.maintenance.service, {
                              type: getType(op.serviceActivityDescription),
                            })}
                          </Tag>
                        ))}
                      </div>
                    );
                  })()}
                </div>
              </span>
            </TimelineEventCard>
          </div>
        ))}
      </Timeline>
    </>
  );
}
